import * as React from 'react';
import { useState, useEffect } from 'react';
import ReactTable from 'react-table';
import {
    PROJECT_STATUS,
    QUICKTAG_STATUS,
    QUICKTAG_STATUS_NUMBER,
    QUICKTAG_STATUS_TYPES,
    QuickTagInstanceReference,
    TENANT_LEVEL
} from '@nexxt/common/types';
import config from '../../config';
import Pagination from '../common/pagination';
import QuickTagCreationWizard from './components/QuickTagCreationWizard';
import { FeedbackPopup } from '../../components/common/Popups';
import * as _ from 'lodash';
import { getTenantLevel } from '@nexxt/common/firebase/auth/userDetails';
import CTA from '../common/CTA';
import http from '../../utils/http';
import { QuickTagCredits } from './types';
import DirectoryCard from '../projects/DirectoryCard';
import Fuse from 'fuse.js';
import { UilPlus } from '@iconscout/react-unicons';
import Select from 'react-select';
import SingleValue from '../common/reactSelect/SingleValue';
import IndicatorsContainer from '../common/reactSelect/IndicatorsContainer';
import CodingCard from './CodingCard';

function QuickTagDirectory() {
    const quicktagStatusList = [
        {
            label:
                QUICKTAG_STATUS.ANALYZING.substring(0, 1) +
                QUICKTAG_STATUS.ANALYZING.substring(1).toLowerCase(),
            value: QUICKTAG_STATUS_NUMBER.ANALYZING
        },
        {
            label:
                QUICKTAG_STATUS.READY.substring(0, 1) +
                QUICKTAG_STATUS.READY.substring(1).toLowerCase(),
            value: QUICKTAG_STATUS_NUMBER.READY
        }
    ];
    const sortList = [
        { label: 'Latest', value: -1 },
        { label: 'Oldest', value: 1 }
    ];
    const displayList = [
        { label: 'Display All', value: 0 },
        { label: 'Display Project Associated Only', value: 1 },
        { label: 'Display Single Instances Only', value: 2 }
    ];
    const tenantLevel = getTenantLevel();
    const [showFeedbackPopup, setShowFeedbackPopup] = React.useState(false);
    const [filteredProjects, setFilteredProjects] = useState([]);

    const [quickTagInstanceReferences, setQuickTagInstanceReferences] =
        useState([]);
    const [showQuickTagCreationWizard, setShowQuickTagCreationWizard] =
        useState(false);
    const [quickTagCredits, setQuickTagCredits] = useState<QuickTagCredits>({
        creditsUsed: '--',
        creditsTotal: '--'
    });
    const [showExistingForm, setShowExistingForm] = useState(false);

    const [state, setState] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    const [selectedProjectFilter, setSelectedProjectFilter] = useState(
        displayList[0]
    );
    const [searchValue, setSearchValue] = useState(null);
    const [sortBy, setSortBy] = useState(sortList[0]);
    const [projects, setProjects] = useState(displayList[0]);
    const [tenantUsers, setTenantUsers] = useState([]);

    const loadCredits = async () => {
        try {
            const url = config.api.endpoints.listQuickTagCredits;
            const response = await http.get(url);
            setQuickTagCredits(response.data.data);
        } catch (err) {
            console.error(err);
        }
    };

    const loadInstances = async () => {
        try {
            const url = config.api.endpoints.listQuickTagInstances;
            const response = await http.get(url);
            setQuickTagInstanceReferences(response.data.data);
            setLoaded(true);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        loadInstances();
        loadCredits();
    }, []);

    useEffect(() => {
        const filteredByDropdown = quickTagInstanceReferences.filter((proj) => {
            const statusMatch = selectedStatusFilter
                ? selectedStatusFilter.value == proj.Status
                : true;

            const projectMatch =
                selectedProjectFilter.value == displayList[1].value
                    ? !_.isNull(proj.PackageID)
                    : selectedProjectFilter.value == displayList[2].value
                    ? _.isNull(proj.PackageID)
                    : true;
            return statusMatch && projectMatch;
        });
        let tempFilteredProjects = filteredByDropdown;
        if (searchValue) {
            const filteredProjectsWithFilterKey = filteredByDropdown.map(
                (p) => ({
                    ...p,
                    searchKey: `${p.StudyName} ${p.QuestionLabel} ${p.QuickTagInstanceID} ${p.QuestionID}`
                })
            );
            const searchOptions = {
                includeMatches: true,
                includeScore: true,
                ignoreLocation: true,
                keys: [
                    'StudyName',
                    'QuestionLabel',
                    'QuickTagInstanceID',
                    'QuestionID'
                ],
                threshold: 0
            };

            const fuse = new Fuse(filteredProjectsWithFilterKey, searchOptions);
            tempFilteredProjects = fuse
                .search(searchValue)
                .map((search) => search.item);
        }
        tempFilteredProjects.sort((a, b) => sortBy.value);

        setFilteredProjects(tempFilteredProjects);
    }, [
        quickTagInstanceReferences,
        searchValue,
        selectedStatusFilter,
        selectedProjectFilter,
        sortBy
    ]);

    const handleRedirect = (instanceID) => {
        window.location.href = `${config.host.quickTag}/?id=${instanceID}`;
    };

    return (
        <>
            <div>
                {tenantLevel === TENANT_LEVEL.INCA_WIZARD && (
                    <div className="quicktag-info">
                        <div className="quicktag-info-description">
                            <span>What is AI Coding?</span>
                            <p>
                                AI Coding is our new multi-dimensional OE
                                verbatim coding tool. It is AI-enabled and
                                researcher-assisted, to help make fully grasping
                                the richness of human language pertinent to your
                                specific research objectives a breeze.
                            </p>
                        </div>
                        <div className="quicktag-info-segments">
                            <div className="quicktag-info-segments-item">
                                <span>AI-powered Chunking</span>
                                <p>
                                    Smart chunk your verbatims with natural
                                    language processing.
                                </p>
                            </div>
                            <div className="quicktag-info-segments-item">
                                <span>Tags &amp; Themes</span>
                                <p>
                                    Conveniently tag verbatims with custom
                                    themes and keywords.
                                </p>
                            </div>
                            <div className="quicktag-info-segments-item">
                                <span>Collaborate (Coming Soon)</span>
                                <p>
                                    Work together closely with your team to tag
                                    large data sets.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {showFeedbackPopup && (
                    <FeedbackPopup
                        title="Leave us a message"
                        text="Got any comments, questions or concerns? Let us know and we will get back to you."
                        close={() => setShowFeedbackPopup(false)}
                    />
                )}
                {showQuickTagCreationWizard && (
                    <QuickTagCreationWizard
                        showQuickTagCreationWizard={showQuickTagCreationWizard}
                        setShowQuickTagCreationWizard={
                            setShowQuickTagCreationWizard
                        }
                        quickTagCredits={quickTagCredits}
                    />
                )}
                <div className="card-grid-container card-contianer--project">
                    <div className="flex gap-4 justify-between mb-4">
                        <div className="filters mb-0">
                            <div className="filters--search">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Search by Project Name, URL or ID"
                                    onChange={(e) =>
                                        setSearchValue(e.target.value)
                                    }
                                />
                                <i className="nx nx-search" />
                            </div>
                            <div className="filters--filter-status">
                                <Select
                                    name="form-field-name"
                                    value={selectedStatusFilter}
                                    options={quicktagStatusList}
                                    onChange={(selection) =>
                                        setSelectedStatusFilter(selection)
                                    }
                                    placeholder="Select Status"
                                    components={{
                                        SingleValue,
                                        FilterValue: () => <span></span>,
                                        IndicatorsContainer
                                    }}
                                    classNamePrefix="react-select"
                                    className="react-select"
                                    //menuIsOpen={true}
                                />
                            </div>
                            <div className="filters--filter-client">
                                <Select
                                    className="react-select"
                                    name="form-field-name"
                                    value={selectedProjectFilter}
                                    options={displayList}
                                    onChange={(selection) =>
                                        setSelectedProjectFilter(selection)
                                    }
                                    placeholder={`Select Instances to Display`}
                                    components={{
                                        SingleValue,
                                        FilterValue: () => <span></span>,
                                        IndicatorsContainer
                                    }}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="filters--filter-client">
                                <Select
                                    className="react-select"
                                    name="form-field-name"
                                    value={sortBy}
                                    options={sortList}
                                    onChange={(selection) =>
                                        setSortBy(selection)
                                    }
                                    placeholder={`Sort By`}
                                    components={{
                                        SingleValue,
                                        FilterValue: () => (
                                            <span>Sort By </span>
                                        ),
                                        IndicatorsContainer
                                    }}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="form-group m-0">
                                <button
                                    disabled={
                                        !selectedStatusFilter && !searchValue
                                    }
                                    type="button"
                                    className="btn btn--md btn--blue"
                                    onClick={() => {
                                        setSelectedStatusFilter(null);
                                        setSearchValue(null);
                                    }}
                                >
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <span className="whitespace-nowrap">
                                {`AI Credits Available: ${
                                    Number(quickTagCredits.creditsTotal) -
                                    Number(quickTagCredits.creditsUsed)
                                }`}
                            </span>
                        </div>
                    </div>

                    <div className="cards grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                        <div
                            className="h-56 w-full cursor-pointer bg-blue text-white font-semibold rounded text-xl flex flex-col justify-center items-center hover:no-underline hover:drop-shadow-md"
                            onClick={() => setShowQuickTagCreationWizard(true)}
                        >
                            <UilPlus size={48} />
                            <span className="card-text mt-2">
                                New AI Coding
                            </span>
                        </div>
                        {filteredProjects.map((project, i) => (
                            <CodingCard key={i} project={project} />
                        ))}
                    </div>
                </div>
            </div>
            {tenantLevel === TENANT_LEVEL.INCA_WIZARD && <CTA />}
        </>
    );
}

export default QuickTagDirectory;
